"use client"

import { useEffect, useMemo, useState } from "react"
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Trash2,
} from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ImageUpload } from "@/components/ui/image-upload"
import { RestartOnboardingCard } from "@/components/onboarding/restart-onboarding-card"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { useAuth } from "@/hooks/use-auth"
import { useCurrentTenantQuery } from "@/hooks/queries/current-tenant"
import { useUpdateTenant } from "@/hooks/use-update-tenant"
import { usePermissions } from "@/hooks/use-permissions"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import {
  buildTimezoneOptions,
  withCustomTimezone,
  type TimezoneOption,
} from "@/lib/utils/timezones"
import { cn } from "@/lib/utils"
import { OrganizationStats } from "@/components/organization/organization-stats"
import { AiDisclosureSettings } from "@/components/organization/ai-disclosure-settings"

/**
 * Organization tab rendered inside the user-profile page. Visibility is
 * gated by the `tenants.view_own` permission at the parent level; this
 * component assumes the caller already checked.
 *
 * Layout (top-to-bottom):
 *   1. `<OrganizationStats />` — read-only overview cards.
 *   2. Organization info card — name, logo, timezone (owner-only mutate).
 *   3. Shared AI disclosure settings and delivery report.
 *   4. Danger zone — type-to-confirm self-archive (owner-only).
 *
 * No outer `p-6 max-w-4xl` wrapper — the host (user-profile page) owns
 * the page chrome.
 */
export function OrganizationTab() {
  const { user, fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const { tenant, isLoading, error, refresh } = useCurrentTenantQuery()
  const {
    isSaving,
    isUploadingLogo,
    error: mutationError,
    updateTenant,
    uploadLogo,
    removeLogo,
    resetError,
  } = useUpdateTenant()

  const [name, setName] = useState("")
  const [timezone, setTimezone] = useState("UTC")
  const [workingHoursStart, setWorkingHoursStart] = useState("10:00")
  const [workingHoursEnd, setWorkingHoursEnd] = useState("17:00")
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false)
  const [timezoneSearch, setTimezoneSearch] = useState("")
  const [status, setStatus] = useState<
    { type: "success" | "error"; message: string } | null
  >(null)
  const [confirmDeleteText, setConfirmDeleteText] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenant) return
    setName(tenant.name)
    setTimezone(tenant.timezone ?? "UTC")
    setWorkingHoursStart(tenant.working_hours_start?.slice(0, 5) ?? "10:00")
    setWorkingHoursEnd(tenant.working_hours_end?.slice(0, 5) ?? "17:00")
    resetError()
    setStatus(null)
    setConfirmDeleteText("")
    setDeleteError(null)
  }, [tenant, resetError])

  const baseTimezoneOptions = useMemo<TimezoneOption[]>(
    () => buildTimezoneOptions(),
    [],
  )
  const timezoneOptions = useMemo(
    () => withCustomTimezone(baseTimezoneOptions, timezone),
    [baseTimezoneOptions, timezone],
  )
  const filteredTimezoneOptions = useMemo(() => {
    const query = timezoneSearch.trim().toLowerCase()
    if (!query) return timezoneOptions
    return timezoneOptions.filter((option) =>
      option.searchText.includes(query),
    )
  }, [timezoneOptions, timezoneSearch])
  const selectedTimezoneOption = useMemo(
    () => timezoneOptions.find((option) => option.value === timezone),
    [timezoneOptions, timezone],
  )

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading organization…</span>
      </div>
    )
  }

  // Guard on the tenant alone: a failed background refetch must not replace a
  // loaded form with an error card.
  if (!tenant) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-destructive">
          {error?.message ?? "Organization not found."}
        </CardContent>
      </Card>
    )
  }

  const isOwner =
    !!user && !!tenant.owner_user_id && tenant.owner_user_id === user.user_id
  const isSuperUser = !!user?.isSuperUser
  const isArchived = !!tenant.archived_at
  const isArchivedBlocked = isArchived && !isSuperUser
  const canMutate = (isOwner || isSuperUser) && !isArchivedBlocked
  const canRestartOnboarding =
    can(TENANTS_PERMISSIONS.EDIT_OWN) && !isArchivedBlocked

  const trimmedName = name.trim()
  const baseTimezone = tenant.timezone ?? "UTC"
  const baseWorkingHoursStart = tenant.working_hours_start?.slice(0, 5) ?? "10:00"
  const baseWorkingHoursEnd = tenant.working_hours_end?.slice(0, 5) ?? "17:00"
  const isDirty =
    trimmedName !== tenant.name ||
    timezone !== baseTimezone ||
    workingHoursStart !== baseWorkingHoursStart ||
    workingHoursEnd !== baseWorkingHoursEnd
  const hasValidWorkingHours = workingHoursStart < workingHoursEnd
  const canSave = canMutate && isDirty && trimmedName.length > 0 && hasValidWorkingHours && !isSaving

  const handleSave = async () => {
    if (!canSave) return
    setStatus(null)
    try {
      await updateTenant(tenant.id, {
        name: trimmedName,
        timezone,
        working_hours_start: workingHoursStart,
        working_hours_end: workingHoursEnd,
      })
      await refresh()
      setStatus({ type: "success", message: "Organization updated." })
    } catch {
      /* error rendered from `mutationError` */
    }
  }

  const handleLogoUpload = async (file: File) => {
    if (!canMutate) return
    setStatus(null)
    try {
      await uploadLogo(tenant.id, file)
      await refresh()
    } catch {
      /* error rendered from `mutationError` */
    }
  }

  const handleLogoRemove = async () => {
    if (!canMutate) return
    setStatus(null)
    try {
      await removeLogo(tenant.id)
      await refresh()
    } catch {
      /* error rendered from `mutationError` */
    }
  }

  const canDelete = isOwner && !isArchived
  const isDeleteConfirmed = confirmDeleteText === tenant.name
  const handleDelete = async () => {
    if (!canDelete || !isDeleteConfirmed) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      const res = await fetchWithAuth(`/api/tenants/${tenant.id}/archive`, {
        method: "POST",
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Failed to delete organization")
      }
      // Every subscriber refetches, so the app shell flips to the archived-warning screen.
      await refresh()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Unknown error")
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      <OrganizationStats />

      <Card>
        <CardHeader>
          <CardTitle>Organization info</CardTitle>
          <CardDescription>
            {canMutate
              ? "Name, logo, time zone, and callback working hours."
              : isArchivedBlocked
                ? "This organization is archived. Settings are read-only."
                : "Only the organization owner can edit these settings."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Logo</Label>
            <ImageUpload
              currentImageUrl={tenant.logo_url ?? null}
              onUpload={handleLogoUpload}
              onRemove={handleLogoRemove}
              isUploading={isUploadingLogo}
              shape="circle"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canMutate}
                placeholder="Organization name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-timezone">Time zone</Label>
              <Popover
                open={isTimezoneOpen}
                onOpenChange={(open) => canMutate && setIsTimezoneOpen(open)}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    id="org-timezone"
                    aria-haspopup="listbox"
                    aria-expanded={isTimezoneOpen}
                    disabled={!canMutate}
                    className={cn(
                      "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                      !selectedTimezoneOption && "text-muted-foreground",
                    )}
                  >
                    <span className="line-clamp-1">
                      {selectedTimezoneOption
                        ? selectedTimezoneOption.label
                        : "Select a time zone"}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                >
                  <Command shouldFilter={false}>
                    <div className="border-b border-border bg-popover">
                      <CommandInput
                        autoFocus
                        value={timezoneSearch}
                        onValueChange={setTimezoneSearch}
                        placeholder="Search time zone..."
                      />
                    </div>
                    <CommandList className="max-h-64 overflow-y-auto">
                      {filteredTimezoneOptions.length > 0 ? (
                        <CommandGroup className="p-0">
                          {filteredTimezoneOptions.map((option) => (
                            <CommandItem
                              key={option.value}
                              value={option.value}
                              onSelect={() => {
                                setTimezone(option.value)
                                setTimezoneSearch("")
                                setIsTimezoneOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  option.value === timezone
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="line-clamp-2">
                                {option.label}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                          No matching time zones
                        </div>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Seasonal clock changes are applied automatically for the
                selected time zone.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label>Callback working hours</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Monday to Friday. Agent Tasks that respect working hours are moved into this local window.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="org-working-hours-start">Working day starts</Label>
                <Input
                  id="org-working-hours-start"
                  type="time"
                  value={workingHoursStart}
                  onChange={event => setWorkingHoursStart(event.target.value)}
                  disabled={!canMutate}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-working-hours-end">Working day ends</Label>
                <Input
                  id="org-working-hours-end"
                  type="time"
                  value={workingHoursEnd}
                  onChange={event => setWorkingHoursEnd(event.target.value)}
                  disabled={!canMutate}
                  aria-invalid={!hasValidWorkingHours}
                />
              </div>
            </div>
            {!hasValidWorkingHours ? (
              <p className="text-sm text-destructive">Working day end must be later than its start.</p>
            ) : null}
          </div>

          {(mutationError || status) && (
            <p
              className={cn(
                "text-sm",
                mutationError
                  ? "text-destructive"
                  : status?.type === "success"
                    ? "text-green-700"
                    : "text-destructive",
              )}
            >
              {mutationError ?? status?.message}
            </p>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button onClick={handleSave} disabled={!canSave}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <span>Save changes</span>
          </Button>
        </CardFooter>
      </Card>

      <AiDisclosureSettings surface="organization" />

      {canRestartOnboarding ? <RestartOnboardingCard /> : null}

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <span>Danger zone</span>
          </CardTitle>
          <CardDescription>
            Delete this organization. Owner only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isOwner ? (
            <p className="text-sm text-muted-foreground">
              Only the organization owner can delete the organization.
            </p>
          ) : isArchived ? (
            <p className="text-sm text-muted-foreground">
              This organization is already scheduled for deletion.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This starts deletion of the organization: services
                (voice / email / messaging agents, phone numbers, billing
                subscription) are disabled, and new sign-ins or session
                refreshes for members are blocked. Existing browser sessions
                lose access as their tokens expire. Data is retained for 30
                days.
              </p>
              <div className="space-y-2">
                <Label htmlFor="confirm-delete-org" className="text-destructive">
                  <span>Type</span>{" "}
                  <span className="font-mono font-bold">{tenant.name}</span> <span>to
                  confirm</span>
                </Label>
                <Input
                  id="confirm-delete-org"
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  placeholder="Organization name"
                  disabled={isDeleting}
                  autoComplete="off"
                />
              </div>
              {deleteError && (
                <p className="text-sm text-destructive">{deleteError}</p>
              )}
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={!isDeleteConfirmed || isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                <span>Delete organization</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
